import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { HomeLayoutAdminEditor } from "@/pages/settings/home-layout/HomeLayoutAdminEditor.tsx";
import {
  useOrganizationHomeLayoutSettings,
  useOrganizationUpdateHomeLayoutSettings,
} from "@/queries/settings/use-organization.query.ts";
import { ApiOrganizationHomeLayoutWidget } from "@/types/organization.types.ts";
import { Button, Page, Skeleton, Stack, Title, useToasts } from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface FormValues {
  widgets: ApiOrganizationHomeLayoutWidget[];
}

export default function HomeLayoutSettings() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { data: settings, isLoading } = useOrganizationHomeLayoutSettings();

  useTitle(t("core:settings_home_layout_title"), t("common:settings"));

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { widgets: [] },
  });

  const updateHomeLayout = useOrganizationUpdateHomeLayoutSettings(control);

  useEffect(() => {
    if (settings) {
      reset({ widgets: settings.widgets });
    }
  }, [settings, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const widgets = data.widgets.map(({ id, hidden, locked }) => ({ id, hidden, locked }));
      await updateHomeLayout.mutateAsync({ widgets });
      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  if (isLoading) {
    return (
      <Page>
        <Page.Header
          title={t("core:settings_home_layout_title")}
          showBack={true}
          navigateTo={"/settings/general"}
        />
        <Page.Content>
          <Stack className={"container-sm mx-auto"} gap={"lg"}>
            <Skeleton className={"h-32 w-full"} />
            <Skeleton className={"h-32 w-full"} />
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header
        title={t("core:settings_home_layout_title")}
        showBack={true}
        navigateTo={"/settings/general"}
      />
      <Page.Content>
        <Stack gap={"xl"} className={"container-sm mx-auto"}>
          <Title
            size={"lg"}
            paddingTop
            paddingBottom
            header={t("core:settings_home_layout_title")}
            caption={t("core:settings_home_layout_description")}
          />

          <form onSubmit={onSubmit}>
            <Stack gap={"lg"}>
              <Controller
                control={control}
                name={"widgets"}
                render={({ field: { value, onChange } }) => (
                  <HomeLayoutAdminEditor widgets={value} onChange={onChange} />
                )}
              />
              <ServerErrorField errors={errors} />
              <Button size={"lg"} type={"submit"} isLoading={updateHomeLayout.isPending}>
                {t("common:save")}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Page.Content>
    </Page>
  );
}
